import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw } from "lucide-react";
import { useSupabase } from "@/hooks/useSupabase";
import { toast } from "sonner";

interface ConciliacaoRow {
  ano: number;
  centro_custo_id: string;
  tipo_despesa_id: string;
  total_legado: number;
  total_v2: number;
  diferenca: number;
}

export default function ValidacaoOrcamentoV2Page() {
  const { supabase } = useSupabase();
  const [ano, setAno] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ConciliacaoRow[]>([]);
  const [countLegacy, setCountLegacy] = useState<number>(0);
  const [countV2, setCountV2] = useState<number>(0);
  const [salvandoAuditoria, setSalvandoAuditoria] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const anoParam = ano.trim() ? Number(ano.trim()) : null;
      const { data, error } = await supabase.rpc("solis_validar_conciliacao_orcamento_v2", {
        p_ano: Number.isFinite(anoParam as number) ? anoParam : null,
      });
      if (error) throw error;
      setRows((data || []) as ConciliacaoRow[]);

      const legacyQuery = supabase
        .from("solis_vw_orcamento_por_tipo_centro_com_chave")
        .select("*", { count: "exact", head: true });
      const v2Query = supabase
        .from("solis_vw_orcamento_por_subtipo_centro_com_chave_v2")
        .select("*", { count: "exact", head: true });

      const [legacyRes, v2Res] = await Promise.all([
        anoParam ? legacyQuery.eq("ano", anoParam) : legacyQuery,
        anoParam ? v2Query.eq("ano", anoParam) : v2Query,
      ]);

      if (legacyRes.error) throw legacyRes.error;
      if (v2Res.error) throw v2Res.error;
      setCountLegacy(legacyRes.count || 0);
      setCountV2(v2Res.count || 0);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao validar conciliação V2");
    } finally {
      setLoading(false);
    }
  };

  const registrarAuditoria = async () => {
    setSalvandoAuditoria(true);
    try {
      const anoParam = ano.trim() ? Number(ano.trim()) : null;
      const { data, error } = await supabase.rpc("solis_registrar_conciliacao_orcamento_v2", {
        p_ano: Number.isFinite(anoParam as number) ? anoParam : null,
      });
      if (error) throw error;
      toast.success(`Auditoria registrada com ${data || 0} divergência(s).`);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao registrar auditoria V2");
    } finally {
      setSalvandoAuditoria(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const divergencias = rows.filter((r) => Number(r.diferenca) !== 0);
  const totalLegado = rows.reduce((acc, r) => acc + Number(r.total_legado || 0), 0);
  const totalV2 = rows.reduce((acc, r) => acc + Number(r.total_v2 || 0), 0);

  return (
    <div className="space-y-4 p-6 max-w-7xl w-full mx-auto">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Validação V2 (Subtipos)</h2>
        <p className="text-muted-foreground text-sm">
          Conciliação entre legado (por tipo) e V2 (por subtipo), sem escrita.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Parâmetros</CardTitle>
          <CardDescription>Filtre por ano para validar a estrutura nova em paralelo.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="ano">Ano (opcional)</Label>
            <Input
              id="ano"
              placeholder="2026"
              value={ano}
              onChange={(e) => setAno(e.target.value)}
              className="w-40"
            />
          </div>
          <Button onClick={carregar} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Atualizar validação
          </Button>
          <Button variant="outline" onClick={registrarAuditoria} disabled={salvandoAuditoria || loading}>
            {salvandoAuditoria ? "Registrando..." : "Registrar auditoria"}
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardHeader><CardTitle className="text-sm">Registros Legado</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{countLegacy}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">Registros V2</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{countV2}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">Total Orçado Legado</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{totalLegado.toFixed(2)}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">Total Orçado V2</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{totalV2.toFixed(2)}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Divergências ({divergencias.length})</CardTitle>
          <CardDescription>Linhas com diferença entre agregação legado e V2.</CardDescription>
        </CardHeader>
        <CardContent>
          {divergencias.length === 0 ? (
            <div className="text-sm text-muted-foreground">Nenhuma divergência encontrada.</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ano</TableHead>
                    <TableHead>Centro Custo</TableHead>
                    <TableHead>Tipo Despesa</TableHead>
                    <TableHead>Legado</TableHead>
                    <TableHead>V2</TableHead>
                    <TableHead>Diferença</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {divergencias.map((r, idx) => (
                    <TableRow key={`${r.ano}-${r.centro_custo_id}-${r.tipo_despesa_id}-${idx}`}>
                      <TableCell>{r.ano}</TableCell>
                      <TableCell>{r.centro_custo_id}</TableCell>
                      <TableCell>{r.tipo_despesa_id}</TableCell>
                      <TableCell>{Number(r.total_legado).toFixed(2)}</TableCell>
                      <TableCell>{Number(r.total_v2).toFixed(2)}</TableCell>
                      <TableCell className="text-red-600">{Number(r.diferenca).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
